import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, AlertTriangle, ShoppingCart, RefreshCw, Search, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/adminConfig";

export default function CloverTest() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, preview, previewing } = useCart();
  const [paying, setPaying] = useState(false);
  const [pending, setPending] = useState([]);
  const [reconciling, setReconciling] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [inspectId, setInspectId] = useState("");
  const [inspectData, setInspectData] = useState(null);
  const [inspectError, setInspectError] = useState("");
  const [repairOrderId, setRepairOrderId] = useState("");
  const [repairPaymentId, setRepairPaymentId] = useState("");
  const [repairing, setRepairing] = useState(false);
  const [avail, setAvail] = useState(null);
  const [testDate, setTestDate] = useState("");
  const [auditing, setAuditing] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditError, setAuditError] = useState("");

  const admin = isAdmin(user);

  const loadPending = useCallback(async () => {
    try {
      const { data } = await api.get("/clover/pending");
      setPending(data.orders || []);
    } catch (e) {
      console.error("Failed to load pending Clover orders:", e);
    }
  }, []);

  useEffect(() => {
    if (admin) {
      loadPending();
      api.get("/clover/delivery-availability").then((r) => {
        setAvail(r.data);
        setTestDate(r.data.earliest_date || "");
      }).catch(() => {});
    }
  }, [admin, loadPending]);

  if (!admin) {
    return (
      <div className="max-w-md mx-auto text-center py-20" data-testid="clover-not-authorized">
        <AlertTriangle className="mx-auto text-rose-400 mb-4" size={40} />
        <h1 className="font-display font-bold text-2xl text-slate-800">Not authorized</h1>
        <p className="text-slate-500 mt-2">Clover checkout is in admin-only test mode.</p>
      </div>
    );
  }

  const pay = async () => {
    if (!testDate) {
      toast.error("Pick a delivery date first.");
      return;
    }
    setPaying(true);
    try {
      const payload = {
        items: items.map((i) => ({
          meal_id: i.meal_id,
          quantity: i.quantity,
          ...(i.config ? { config: i.config } : {}),
        })),
        delivery_date: testDate,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/clover/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start Clover checkout.");
      setPaying(false);
    }
  };

  const reconcile = async (orderId) => {
    setReconciling(orderId);
    try {
      const { data } = await api.post(`/clover/reconcile/${orderId}`);
      if (data.status === "paid") {
        toast.success(`Order ${orderId} verified & marked paid.`);
        loadPending();
      } else {
        toast.info("Order not yet verifiable.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not verify this payment with Clover.");
    } finally {
      setReconciling(null);
    }
  };

  const inspectPayments = async () => {
    setInspecting(true);
    setInspectError("");
    setInspectData(null);
    try {
      const params = inspectId.trim()
        ? { payment_id: inspectId.trim() }
        : { limit: 10 };
      const { data } = await api.get("/clover/admin/payment-inspect", { params });
      setInspectData(data);
    } catch (err) {
      setInspectError(err.response?.data?.detail || "Clover inspection failed.");
    } finally {
      setInspecting(false);
    }
  };

  const runAudit = async () => {
    setAuditing(true);
    setAuditError("");
    setAuditData(null);
    try {
      const { data } = await api.get("/clover/admin/audit-unresolved");
      setAuditData(data);
    } catch (err) {
      setAuditError(err.response?.data?.detail || "Audit failed.");
    } finally {
      setAuditing(false);
    }
  };

  const classBadge = (c) => {
    if (c === "Paid evidence found") return "bg-emerald-100 text-emerald-700";
    if (c === "Failed/Abandoned evidence") return "bg-slate-200 text-slate-600";
    return "bg-amber-100 text-amber-700";
  };

  const repairByPaymentId = async () => {
    const oid = repairOrderId.trim();
    const pid = repairPaymentId.trim();
    if (!oid || !pid) {
      toast.error("Enter both the local order ID and the Clover Payment ID.");
      return;
    }
    setRepairing(true);
    try {
      const { data } = await api.post(`/clover/admin/reconcile-by-payment/${encodeURIComponent(pid)}`, {
        order_id: oid,
      });
      if (data.status === "paid") {
        toast.success(`Order ${oid} marked paid via payment ${pid}.`);
        loadPending();
      } else {
        toast.info("Could not mark paid.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Repair failed.");
    } finally {
      setRepairing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="clover-test-page">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center h-11 w-11 rounded-2xl bg-indigo-100 text-indigo-600">
          <FlaskConical size={22} />
        </span>
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800">Clover Checkout — Admin Test</h1>
          <p className="text-slate-500 text-sm">Admin diagnostics & test checkout. Customers now check out with Clover.</p>
        </div>
      </div>

      <div
        data-testid="clover-live-warning"
        className="flex gap-3 rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 text-amber-800"
      >
        <AlertTriangle size={20} className="shrink-0 mt-0.5" />
        <p className="text-sm">
          If Clover is set to <strong>production</strong>, this charges a <strong>real card for real money</strong>.
          Use a small order and, ideally, refund it afterward from your Clover dashboard.
        </p>
      </div>

      <div className="bg-white rounded-3xl border-2 border-emerald-100 p-6">
        <h2 className="font-display font-bold text-xl text-slate-800 mb-3">Current cart</h2>
        {items.length === 0 ? (
          <div className="text-center py-8">
            <ShoppingCart className="mx-auto text-slate-300 mb-3" size={36} />
            <p className="text-slate-500">Your cart is empty. Add items from the menu to test a real amount.</p>
            <button
              data-testid="clover-browse-menu"
              onClick={() => navigate("/menu")}
              className="mt-4 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-2xl px-5 py-2.5"
            >
              Browse the Menu
            </button>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-slate-100 mb-4">
              {items.map((i) => (
                <li key={i.key} className="flex justify-between py-2 text-slate-600">
                  <span>{i.name} × {i.quantity}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between border-t-2 border-slate-100 pt-3">
              <span className="font-display font-bold text-lg text-slate-800">Total</span>
              <span data-testid="clover-total" className="font-display font-bold text-2xl text-indigo-600">
                {formatPrice(preview?.total_cents)}
              </span>
            </div>
            <div className="mt-4">
              <label className="block font-semibold text-slate-700 text-sm mb-1.5">Delivery date</label>
              <input
                type="date"
                data-testid="clover-delivery-date-input"
                value={testDate}
                min={avail?.earliest_date}
                max={avail?.latest_date}
                onChange={(e) => setTestDate(e.target.value)}
                className="w-full rounded-2xl border-2 border-slate-200 px-4 py-2.5 text-sm focus:border-indigo-400 focus:outline-none"
              />
            </div>
            <button
              data-testid="clover-pay-button"
              onClick={pay}
              disabled={paying || previewing || !preview || !testDate}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-display font-bold text-lg rounded-2xl py-3.5 shadow-md transition-all active:scale-[0.98]"
            >
              {paying ? "Redirecting to Clover…" : `Pay ${formatPrice(preview?.total_cents)} with Clover`}
            </button>
          </>
        )}
      </div>

      {/* Pending orders — reconcile a captured payment whose webhook was missed */}
      <div className="bg-white rounded-3xl border-2 border-emerald-100 p-6" data-testid="clover-pending-panel">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-xl text-slate-800">Pending Clover orders</h2>
          <button
            data-testid="clover-pending-refresh"
            onClick={loadPending}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-600"
          >
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
        {pending.length === 0 ? (
          <p className="text-slate-400 text-sm">No pending orders. 🎉</p>
        ) : (
          <ul className="space-y-3">
            {pending.map((o) => (
              <li
                key={o.order_id}
                data-testid={`clover-pending-${o.order_id}`}
                className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">{formatPrice(o.amount_cents)}</p>
                  <p className="text-xs text-slate-400 truncate">{o.order_id}</p>
                  <p className="text-xs text-slate-400 truncate">session: {o.checkout_session_id}</p>
                </div>
                <button
                  data-testid={`clover-reconcile-${o.order_id}`}
                  onClick={() => reconcile(o.order_id)}
                  disabled={reconciling === o.order_id}
                  className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold text-sm rounded-2xl px-4 py-2"
                >
                  {reconciling === o.order_id ? "Verifying…" : "Verify & reconcile"}
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-slate-400 mt-3">
          "Verify &amp; reconcile" asks the backend to confirm the payment directly with Clover before marking it paid. It never creates or retries a charge.
        </p>
      </div>

      {/* READ-ONLY historical order audit — no writes, no payments, no email */}
      <div className="bg-white rounded-3xl border-2 border-sky-100 p-6" data-testid="clover-audit-panel">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ClipboardList size={18} className="text-sky-600" />
            <h2 className="font-display font-bold text-xl text-slate-800">Unresolved order audit</h2>
          </div>
          <button
            data-testid="clover-audit-button"
            onClick={runAudit}
            disabled={auditing}
            className="shrink-0 inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white font-semibold text-sm rounded-2xl px-5 py-2.5"
          >
            {auditing ? "Auditing…" : "Run audit"}
          </button>
        </div>
        <p className="text-slate-500 text-sm mb-4">
          Read-only. Lists every unresolved (non-paid) order and, for each, does a deterministic
          reference-only cross-check against Clover. It makes no payment, repairs nothing, sends no
          email, and writes nothing to the database. Amount is never used to establish a payment.
        </p>

        {auditError && (
          <p data-testid="clover-audit-error" className="mt-1 text-sm text-rose-500">{auditError}</p>
        )}

        {auditData && (
          <div className="mt-2" data-testid="clover-audit-result">
            <div className="flex flex-wrap gap-2 mb-3 text-xs">
              <span className="rounded-full bg-slate-100 text-slate-600 px-3 py-1">env: {auditData.env}</span>
              <span className="rounded-full bg-slate-100 text-slate-600 px-3 py-1">unresolved: {auditData.unresolved_count}</span>
              <span className={`rounded-full px-3 py-1 ${auditData.clover_query_available ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                clover reachable: {String(auditData.clover_query_available)}
              </span>
            </div>
            {(auditData.orders || []).length === 0 ? (
              <p className="text-slate-400 text-sm">No unresolved orders. 🎉</p>
            ) : (
              <ul className="space-y-3">
                {auditData.orders.map((o) => (
                  <li
                    key={o.order_id}
                    data-testid={`clover-audit-${o.order_id}`}
                    className="rounded-2xl bg-slate-50 px-4 py-3 text-sm"
                  >
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="font-mono text-xs text-slate-500 truncate">{o.order_id}</span>
                      <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${classBadge(o.classification)}`}>
                        {o.classification}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-slate-600">
                      <span>Amount: <strong>{o.amount}</strong></span>
                      <span>Local status: <strong>{o.local_status}</strong> ({o.display_status})</span>
                      <span className="truncate">Customer: {o.customer || "—"}</span>
                      <span>Created: {o.created_at}</span>
                      <span>Stored payment ID: {o.stored_clover_payment_id || "—"}</span>
                      <span>Stored order ID: {o.stored_clover_order_id || "—"}</span>
                      <span>Clover payment established: <strong>{String(o.exact_clover_payment_established)}</strong></span>
                      <span>Est. payment: {o.established_clover_payment_id || "—"} ({o.established_clover_payment_status || "—"})</span>
                      <span>[waj_] ref found: <strong>{String(o.waj_reference_found)}</strong></span>
                      <span className="truncate">Method: {o.evidence_method || "—"}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <pre
              data-testid="clover-audit-json"
              className="mt-4 max-h-[24rem] overflow-auto rounded-2xl bg-slate-900 text-slate-100 text-xs p-4 leading-relaxed"
            >
              {JSON.stringify(auditData, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Step 1 diagnostic — READ-ONLY Clover payment inspector */}
      <div className="bg-white rounded-3xl border-2 border-indigo-100 p-6" data-testid="clover-inspect-panel">
        <div className="flex items-center gap-2 mb-2">
          <Search size={18} className="text-indigo-600" />
          <h2 className="font-display font-bold text-xl text-slate-800">Clover payment inspector</h2>
        </div>
        <p className="text-slate-500 text-sm mb-4">
          Read-only. Fetches recent Clover payments (or one by Payment ID) with the order &amp; line
          items expanded, so we can confirm which identifiers Clover returns. It makes no payment,
          creates no order, and changes no data. Card data and credentials are never shown.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="clover-inspect-id-input"
            value={inspectId}
            onChange={(e) => setInspectId(e.target.value)}
            placeholder="Optional: exact Clover Payment ID (blank = 10 most recent)"
            className="flex-1 rounded-2xl border-2 border-slate-200 px-4 py-2.5 text-sm focus:border-indigo-400 focus:outline-none"
          />
          <button
            data-testid="clover-inspect-button"
            onClick={inspectPayments}
            disabled={inspecting}
            className="shrink-0 inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold text-sm rounded-2xl px-5 py-2.5"
          >
            {inspecting ? "Inspecting…" : "Inspect payments"}
          </button>
        </div>

        {inspectError && (
          <p data-testid="clover-inspect-error" className="mt-3 text-sm text-rose-500">{inspectError}</p>
        )}

        {inspectData && (
          <div className="mt-4" data-testid="clover-inspect-result">
            <div className="flex flex-wrap gap-2 mb-3 text-xs">
              <span className="rounded-full bg-slate-100 text-slate-600 px-3 py-1">env: {inspectData.env}</span>
              <span className="rounded-full bg-slate-100 text-slate-600 px-3 py-1">payments: {inspectData.count}</span>
              <span className={`rounded-full px-3 py-1 ${inspectData.any_lineitem_has_waj_ref ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                line-item carries waj_ ref: {String(inspectData.any_lineitem_has_waj_ref)}
              </span>
            </div>
            <pre
              data-testid="clover-inspect-json"
              className="max-h-[28rem] overflow-auto rounded-2xl bg-slate-900 text-slate-100 text-xs p-4 leading-relaxed"
            >
              {JSON.stringify(inspectData.payments, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Admin-only controlled repair: reconcile an order by exact Clover Payment ID */}
      <div className="bg-white rounded-3xl border-2 border-rose-100 p-6" data-testid="clover-repair-panel">
        <h2 className="font-display font-bold text-xl text-slate-800 mb-2">Repair order by Clover Payment ID</h2>
        <p className="text-slate-500 text-sm mb-4">
          For an order whose Clover line item predates the embedded reference. Provide the exact local
          order ID and the exact Clover Payment ID. The backend verifies the payment is approved, the
          amount matches, the order is unpaid, and the payment isn't already attached elsewhere — then
          marks it paid and notifies the owner. Amount is only a safeguard, never used to find an order.
        </p>
        <div className="space-y-3">
          <input
            data-testid="clover-repair-order-input"
            value={repairOrderId}
            onChange={(e) => setRepairOrderId(e.target.value)}
            placeholder="Local order ID (e.g. waj_c1ccdb0f5fda)"
            className="w-full rounded-2xl border-2 border-slate-200 px-4 py-2.5 text-sm focus:border-rose-400 focus:outline-none"
          />
          <input
            data-testid="clover-repair-payment-input"
            value={repairPaymentId}
            onChange={(e) => setRepairPaymentId(e.target.value)}
            placeholder="Clover Payment ID (e.g. JB6DNGMTHTZ8Y)"
            className="w-full rounded-2xl border-2 border-slate-200 px-4 py-2.5 text-sm focus:border-rose-400 focus:outline-none"
          />
          <button
            data-testid="clover-repair-button"
            onClick={repairByPaymentId}
            disabled={repairing}
            className="w-full inline-flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white font-semibold text-sm rounded-2xl px-5 py-2.5"
          >
            {repairing ? "Verifying with Clover…" : "Verify & repair by Payment ID"}
          </button>
        </div>
      </div>
    </div>
  );
}
